import React, { useEffect, useRef } from 'react';
import cx from 'classnames';
import {
  ComponentTreeNode,
  FlexiblePageTreeNodeType,
  TabGroupTreeNodeData,
  TabListTreeNode,
  TabPanelsTreeNode,
} from '@mutiny-pkg/champaign-schemas/src/schemas/flexiblePages/flexiblePagesTreeNode';
import { transformToContainerQueries } from '../utils/transformBreakpointClasses';

type TabGroupProps = {
  id: string;
  editorClassName?: string;
  isEditor?: boolean;
  renderComponentNode: (treeNode: ComponentTreeNode, parentId: string, index: number) => React.ReactNode;
  tabGroupNodes: [] | [TabListTreeNode, TabPanelsTreeNode] | [TabPanelsTreeNode, TabListTreeNode];
} & TabGroupTreeNodeData;

// TypeScript declarations for Tailwind Plus Elements
/* eslint-disable @typescript-eslint/no-namespace */
declare global {
  namespace JSX {
    interface IntrinsicElements {
      'el-tab-group': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
        class?: string;
        key?: string;
      };
    }
  }
}
/* eslint-enable @typescript-eslint/no-namespace */

export const TabGroup = React.memo(
  React.forwardRef<HTMLDivElement, TabGroupProps>(
    ({ id, className, editorClassName, isEditor, renderComponentNode, tabGroupNodes, ...props }, ref) => {
      const remount = useRef(0);

      useEffect(() => {
        remount.current += 1;
      }, []);

      // Find TabList and TabPanels nodes
      const tabListNode = tabGroupNodes.find((node) => node.type === FlexiblePageTreeNodeType.TabList) as
        | TabListTreeNode
        | undefined;

      const tabPanelsNode = tabGroupNodes.find((node) => node.type === FlexiblePageTreeNodeType.TabPanels) as
        | TabPanelsTreeNode
        | undefined;

      if (!tabListNode || !tabPanelsNode) {
        console.warn('TabGroup requires both TabList and TabPanels nodes');
        return null;
      }

      // Calculate minimum count and truncate children to ensure tabs and panels match
      const minChildCount = Math.min(tabListNode.children?.length ?? 0, tabPanelsNode.children?.length ?? 0);

      const truncatedTabListChildren = tabListNode.children?.slice(0, minChildCount) ?? [];
      const truncatedTabPanelsChildren = tabPanelsNode.children?.slice(0, minChildCount) ?? [];

      // Apply hidden attribute to non-active panels
      const panelsWithHidden = truncatedTabPanelsChildren.map((panel, index) => ({
        ...panel,
        data: {
          ...panel.data,
          hidden: index !== 0,
        },
      }));

      // Create updated nodes with truncated children
      const updatedTabListNode: TabListTreeNode = {
        ...tabListNode,
        children: truncatedTabListChildren,
      };

      const updatedTabPanelsNode: TabPanelsTreeNode = {
        ...tabPanelsNode,
        children: panelsWithHidden,
      };

      // Reconstruct tabGroupNodes tuple in original order
      const updatedTabGroupNodes =
        tabGroupNodes[0]?.type === FlexiblePageTreeNodeType.TabList
          ? ([updatedTabListNode, updatedTabPanelsNode] as [TabListTreeNode, TabPanelsTreeNode])
          : ([updatedTabPanelsNode, updatedTabListNode] as [TabPanelsTreeNode, TabListTreeNode]);

      const tabCount = minChildCount;

      return (
        <el-tab-group
          key={`${id}-${tabCount}-${remount.current}`}
          ref={ref}
          id={id}
          class={cx(transformToContainerQueries(className, { isEditor }), editorClassName)}
          {...props}
        >
          {updatedTabGroupNodes.map((node, nodeIndex) => renderComponentNode(node, id, nodeIndex))}
        </el-tab-group>
      );
    },
  ),
);

TabGroup.displayName = 'TabGroup';
